import React from 'react';
import { Film } from 'lucide-react';
import { useLazyTMDB } from '../../hooks/useLazyTMDB';
import { useMeasuredWidth } from '../../hooks/useMeasuredWidth';
import { buildPosterSrcSet, tmdbIdOf } from '../../services/tmdbService';

// Candidate widths offered to the browser. `sizes` tells it how wide the poster
// actually renders, and it combines that with the device pixel ratio to pick the
// smallest candidate that still looks sharp.
//
// NOTE: `sizes` is measured, never derived from the `size` prop - every call
// site overrides the dimensions via className. See useMeasuredWidth.
const POSTER_SRCSET_WIDTHS = [92, 154, 185, 342, 500];

const MoviePoster = ({ 
  movie, 
  size = 'md', 
  showRating = false, 
  borderColor = 'border-orange-600',
  className = '',
  onClick = null
}) => {
  // Use lazy loading for TMDB data (skip for placeholder movies)
  const { tmdbData, loading: tmdbLoading, elementRef } = useLazyTMDB(
    movie?.isPlaceholder ? null : movie?.title, 
    movie?.isPlaceholder ? null : movie?.year,
    // Lazy-load posters; look up by id when the movie carries one
    { immediate: false, tmdbId: movie?.isPlaceholder ? null : tmdbIdOf(movie) }
  );
  
  // Size configurations. These only set the DEFAULT box - callers routinely
  // override the dimensions via className, so they are not used for `sizes`.
  const sizeConfigs = {
    sm: { container: 'w-12 h-16', rating: 'text-xs px-1 py-0.5', fallbackText: 'text-xs' },
    md: { container: 'w-16 h-20', rating: 'text-xs px-2 py-1', fallbackText: 'text-sm' },
    lg: { container: 'w-20 h-28', rating: 'text-sm px-2 py-1', fallbackText: 'text-base' },
    xl: { container: 'w-32 h-48', rating: 'text-sm px-2 py-1', fallbackText: 'text-xl' }
  };

  const config = sizeConfigs[size] || sizeConfigs.md;

  const [measuredWidth, setWrapperRef] = useMeasuredWidth(elementRef);

  return (
    <div
      ref={setWrapperRef}
      className={`relative ${config.container} rounded border ${borderColor} overflow-hidden shadow-lg ${onClick ? 'cursor-pointer hover:opacity-80' : ''} transition-opacity ${className}`}
      onClick={onClick}
    >
      {tmdbData?.posterUrl ? (
        <>
          <img
            src={tmdbData.posterUrl}
            srcSet={measuredWidth ? buildPosterSrcSet(tmdbData.posterUrl, POSTER_SRCSET_WIDTHS) : undefined}
            sizes={measuredWidth ? `${measuredWidth}px` : undefined}
            alt={`${movie.title} poster`}
            className="w-full h-full object-cover"
            decoding="async"
          />
          {/* TMDB Rating Badge */}
          {showRating && tmdbData.rating && (
            <div className={`absolute top-1 right-1 bg-yellow-600 text-white font-bold ${config.rating} rounded-full shadow-lg`}>
              ⭐ {tmdbData.rating.toFixed(1)}
            </div>
          )}
          {/* Loading overlay */}
          {tmdbLoading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <div className="text-white text-xs">Loading...</div>
            </div>
          )}
        </>
      ) : (
        <div className={`w-full h-full ${movie.color} flex items-center justify-center`}>
          {tmdbLoading ? (
            <div className="text-white text-center">
              <Film className="w-4 h-4 mx-auto mb-1 animate-pulse" />
              <div className="text-xs">Loading...</div>
            </div>
          ) : (
            <span className={`text-white font-bold text-center px-1 leading-tight ${config.fallbackText}`}>
              {movie.title.split(' ').map(word => word[0]).join('').slice(0, size === 'sm' ? 3 : 4)}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default MoviePoster;
